/* @flow */

import clsx from 'clsx';
import * as React from 'react';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useResizeDetector } from 'react-resize-detector';

import Localizer from '../../helpers/localization';
import { AVENUE_ID } from '../../helpers/ui/avenue/constants';
import { AvenueType } from '../../helpers/ui/avenue/types';
import { BURGER_WIDTH } from '../../helpers/ui/constants';
import getTranslatedText from '../../libs/netgemLibrary/v8/helpers/Lang';
import { RegistrationType } from '../../redux/appRegistration/types/types';
import AccessibleButton from '../buttons/AccessibleButton';
import { PictoArrowDown } from '../pictos/Element';
import MenuItem, { type MenuItemHandle } from './Item';
import './NavigationMenu.css';

export const NO_AVENUE_INDEX = -1;

type MenuItemType = {
  index: number,
  text: string,
  type: AvenueType,
};

const NavigationMenu = (): React.Node => {
  const avenueList = useSelector((s) => s.ui.avenueList);
  const isGuest = useSelector((s) => s.appRegistration.registration === RegistrationType.RegisteredAsGuest);

  const [visibleItems, setVisibleItems] = useState<Array<MenuItemType>>([]);
  const [hiddenItems, setHiddenItems] = useState<Array<MenuItemType>>([]);
  const [menuWidthState, setMenuWidthState] = useState<number | null>(null);
  const [isBurgerOpen, setIsBurgerOpen] = useState<boolean>(false);

  const itemRefs = useRef<Map<number, MenuItemHandle>>(new Map());
  const burgerRef = useRef<HTMLElement | null>(null);

  // Close burger menu when user clicks anywhere outside the burger element
  useEffect(() => {
    const handleOutsideClick = (event: SyntheticMouseEvent<HTMLElement> | SyntheticTouchEvent<HTMLElement>) => {
      if (burgerRef.current && !burgerRef.current.contains(event.target as any)) {
        setIsBurgerOpen(false);
      }
    };

    if (isBurgerOpen) {
      document.addEventListener('click', handleOutsideClick);
    }

    return () => {
      document.removeEventListener('click', handleOutsideClick);
    };
  }, [isBurgerOpen]);

  useLayoutEffect(() => {
    itemRefs.current.clear();

    const items: Array<MenuItemType> = [];

    avenueList?.forEach((avenue, index) => {
      if (avenue.id === AVENUE_ID.MyVideos && isGuest) return;

      let type = AvenueType.Regular;
      if (avenue.id === AVENUE_ID.Explore) type = AvenueType.Explore;
      if (avenue.id === AVENUE_ID.MyVideos) type = AvenueType.MyVideos;

      items.push({
        index,
        text: getTranslatedText(avenue.title, Localizer.language),
        type,
      });
    });

    setVisibleItems(items);
    setHiddenItems([]);
  }, [avenueList, isGuest]);

  const onResize = useCallback(({ width }: { width: number | null }) => {
    if (width !== null) {
      setMenuWidthState(width);
    }
  }, []);

  const { ref: resizeRef } = useResizeDetector({ handleHeight: false, onResize });

  useLayoutEffect(() => {
    if (!menuWidthState) {
      return;
    }

    const visible = [...visibleItems];
    const hidden = [...hiddenItems];

    const getWidth = (item: MenuItemType) => itemRefs.current.get(item.index)?.getTotalWidth?.() ?? 0;

    let usedWidth = visible.reduce((sum, item) => sum + getWidth(item), 0);

    if (hidden.length > 0 || usedWidth > menuWidthState) {
      // Add burger icon width
      usedWidth += BURGER_WIDTH;
    }

    // Shrinking
    while (usedWidth > menuWidthState && visible.length > 0) {
      const removed = visible.pop();
      if (removed) {
        usedWidth -= getWidth(removed);
        hidden.unshift(removed);
      }
    }

    // Growing
    while (hidden.length > 0) {
      const [next] = hidden;
      const nextWidth = getWidth(next);
      const burgerDelta = hidden.length === 1 ? BURGER_WIDTH : 0;

      if (usedWidth + nextWidth - burgerDelta > menuWidthState) {
        break;
      }

      hidden.shift();
      visible.push(next);
      usedWidth += nextWidth;
    }

    const same = (a: Array<MenuItemType>, b: Array<MenuItemType>): boolean => a.length === b.length && a.every((x, i) => x.index === b[i].index);

    if (!same(visible, visibleItems)) {
      setVisibleItems(visible);
    }
    if (!same(hidden, hiddenItems)) {
      setHiddenItems(hidden);
      if (hidden.length === 0) {
        setIsBurgerOpen(false);
      }
    }
  }, [visibleItems, hiddenItems, menuWidthState]);

  const setItemRef = useCallback(
    (index: number) => (element: MenuItemHandle | null) => {
      if (element) {
        itemRefs.current.set(index, element);
      } else {
        itemRefs.current.delete(index);
      }
    },
    [],
  );

  const toggleBurger = useCallback(() => {
    setIsBurgerOpen((prev) => !prev);
  }, []);

  const handleCloseBurger = useCallback(() => {
    setIsBurgerOpen(false);
  }, []);

  const renderItems = useCallback(
    (items: Array<MenuItemType>, isInBurger: boolean) =>
      items.map(({ index, text, type }) => <MenuItem index={index} key={index} onSelect={isInBurger ? handleCloseBurger : undefined} ref={setItemRef(index)} text={text} type={type} />),
    [setItemRef],
  );

  const burger = useMemo(
    () =>
      hiddenItems.length > 0 && (
        <AccessibleButton aria-expanded={isBurgerOpen} aria-haspopup className={clsx('burgerIcon', isBurgerOpen && 'open')} onClick={toggleBurger} ref={burgerRef}>
          <PictoArrowDown forceHoverEffect />
          <div className='burgerMenu' inert={!isBurgerOpen}>
            {renderItems(hiddenItems, true)}
          </div>
        </AccessibleButton>
      ),
    [hiddenItems, isBurgerOpen, renderItems, toggleBurger],
  );

  return (
    <nav aria-label={Localizer.localize('accessibility.navigation.avenue_links')} className='menuWrapper' ref={resizeRef}>
      {visibleItems.length > 0 ? <ul role='presentation'>{renderItems(visibleItems, false)}</ul> : null}
      {burger}
    </nav>
  );
};

export default NavigationMenu;
